import { memo, useCallback, useEffect, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Button, Collapse, Flex, Input } from '@/lib/v2/components';

import { SendActionsPostMessage } from '@/src/compat/sendActionsPostMessage';
import { ISenderData } from '@/src/presentation/types/interfaces/ISenderData.interface';

import { isValidCorporativeEmail } from '@/modules/ContactsModule/util/VerifyCorporateEmail';

interface FormSenderProps {
  onSubmitFormData: (formValue: FormDataSender) => void;
  defaultValues: FormDataSender;
  isNewSender: boolean;
  isEmailCompany: boolean;
  isLoading: boolean;
}

type FormDataSender = Omit<ISenderData, 'id' | 'default'>;

const FormSender = ({
  onSubmitFormData,
  defaultValues,
  isNewSender,
  isEmailCompany,
  isLoading,
}: FormSenderProps) => {
  const { t } = useTranslation();

  const RULES = {
    nameFrom: {
      required: t('CREATE_SENDER_MODAL.EmptyField'),
    },
    emailFrom: {
      required: t('CREATE_SENDER_MODAL.emailRequired'),
      validate: (value: string) => {
        return isValidCorporativeEmail(value) || t('CREATE_SENDER_MODAL.emailNotAllowed');
      },
    },
    emailReply: {
      required: t('CREATE_SENDER_MODAL.emailRequired'),
      validate: (value: string) => {
        return isValidCorporativeEmail(value) || t('CREATE_SENDER_MODAL.emailNotAllowed');
      },
    },
    cardIndustry: {
      required: t('CREATE_SENDER_MODAL.EmptyField'),
    },
    cardCountry: {
      required: t('CREATE_SENDER_MODAL.EmptyField'),
    },
    cardCity: {
      required: t('CREATE_SENDER_MODAL.EmptyField'),
    },
    cardState: {
      required: t('CREATE_SENDER_MODAL.EmptyField'),
    },
    cardZip: {
      required: t('CREATE_SENDER_MODAL.EmptyField'),
    },
    cardAddress: {
      required: t('CREATE_SENDER_MODAL.EmptyField'),
    },
  };

  const {
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<FormDataSender>({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues,
  });

  const onSubmit: SubmitHandler<FormDataSender> = useCallback(
    (data) => {
      onSubmitFormData(data);
    },
    [onSubmitFormData]
  );

  return (
    <Collapse isOpened>
      <form
        className="flex w-full items-center justify-center gap-3 py-2"
        onSubmit={handleSubmit(onSubmit)}
      >
        <div className="flex w-[300px] flex-col gap-2">
          <Input
            control={control}
            id="nameFrom"
            label={t('CREATE_SENDER_MODAL.senderName')}
            name="nameFrom"
            placeholder={t('CREATE_SENDER_MODAL.senderName')}
            rules={RULES.nameFrom}
            type="text"
          />
          <Input
            control={control}
            disabled={isEmailCompany}
            id="emailFrom"
            label={t('CREATE_SENDER_MODAL.emailFrom')}
            name="emailFrom.email"
            placeholder={t('CREATE_SENDER_MODAL.emailFrom')}
            rules={RULES.emailFrom}
            type="email"
          />
          <Input
            control={control}
            disabled={isEmailCompany}
            id="emailReply"
            label={t('CREATE_SENDER_MODAL.emailReply')}
            name="emailReply.email"
            placeholder={t('CREATE_SENDER_MODAL.emailReply')}
            rules={RULES.emailReply}
            type="email"
          />

          {!isNewSender && (
            <>
              <Input
                control={control}
                id="cardIndustry"
                label={t('CREATE_SENDER_MODAL.company')}
                name="cardIndustry"
                placeholder={t('CREATE_SENDER_MODAL.company')}
                rules={RULES.cardIndustry}
                type="text"
              />
              <Input
                control={control}
                id="cardCountry"
                label={t('CREATE_SENDER_MODAL.country')}
                name="cardCountry"
                placeholder={t('CREATE_SENDER_MODAL.country')}
                rules={RULES.cardCountry}
                type="text"
              />
              <Input
                control={control}
                id="cardCity"
                label={t('CREATE_SENDER_MODAL.city')}
                name="cardCity"
                placeholder={t('CREATE_SENDER_MODAL.city')}
                rules={RULES.cardCity}
                type="text"
              />
              <Input
                control={control}
                id="cardState"
                label={t('CREATE_SENDER_MODAL.state')}
                name="cardState"
                placeholder={t('CREATE_SENDER_MODAL.state')}
                rules={RULES.cardState}
                type="text"
              />
              <Input
                control={control}
                id="cardZip"
                label={t('CREATE_SENDER_MODAL.zipCode')}
                name="cardZip"
                placeholder={t('CREATE_SENDER_MODAL.zipCode')}
                rules={RULES.cardZip}
                type="text"
              />
              <Input
                control={control}
                id="cardAddress"
                label={t('CREATE_SENDER_MODAL.address')}
                name="cardAddress"
                placeholder={t('CREATE_SENDER_MODAL.address')}
                rules={RULES.cardAddress}
                type="text"
              />
            </>
          )}
          <Flex withGap alignment="start" gapSize="small">
            <Button isLoading={isLoading} type="submit">
              {t('CREATE_SENDER_MODAL.save')}
            </Button>
          </Flex>
        </div>
      </form>
    </Collapse>
  );
};

export default memo(FormSender);
